import React, { useState, useEffect } from 'react';
import { useAlerts } from '../contexts/AlertContext';
import { usePractice } from '../contexts/PracticeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { formatTime, getTimerColor, shouldFlash } from '../lib/utils';
import LongPressMenu from './LongPressMenu';
import { Clock, X, AlertCircle, CheckCheck } from 'lucide-react';
import { playSoundForAlert } from '../lib/sounds';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const alertTypeLabels = {
  patient_arrived: 'Arrived',
  seated_in_room: 'Seated in Room',
  dr_needed: 'Dr. Needed',
  patient_checkout: 'Check Out',
  // Legacy types for backwards compatibility
  patient_in_room: 'Seated in Room',
  hygiene_exam_request: 'Dr. Needed',
  assistant_help: 'Help Needed',
  recall_scheduling: 'Recall Scheduling'
};

function AlertBubble({ alert, index, onStatusChange, currentUserId }) {
  const [elapsed, setElapsed] = useState(0);
  const [operatory, setOperatory] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [readReceipts, setReadReceipts] = useState(alert.read_receipts || []);
  const [quickResponses, setQuickResponses] = useState(alert.quick_responses || []);
  const [isMarking, setIsMarking] = useState(false);
  const { completeAlert, fetchAlerts } = useAlerts();
  const { practice } = usePractice();

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const opRes = await axios.get(`${API}/operatories`);
        const op = opRes.data.find(o => o.id === alert.operatory_id);
        setOperatory(op);
        
        // Users list is admin-only — fetch separately
        try {
          const usersRes = await axios.get(`${API}/users`);
          const docs = usersRes.data.filter(u => u.role === 'doctor' || u.base_role === 'doctor');
          setDoctors(docs);
        } catch {}
      } catch (error) {
        console.error('Failed to fetch alert details:', error);
      }
    };

    fetchDetails();
  }, [alert]);

  useEffect(() => {
    const createdAt = new Date(alert.created_at);
    
    const updateTimer = () => {
      const now = new Date();
      const seconds = Math.floor((now - createdAt) / 1000);
      setElapsed(seconds);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [alert.created_at]);

  const warningThreshold = practice?.warning_threshold || 300;
  const criticalThreshold = practice?.critical_threshold || 420;
  
  const timerColor = getTimerColor(elapsed, warningThreshold, criticalThreshold);
  const isFlashing = shouldFlash(elapsed, criticalThreshold);

  const getBgColor = () => {
    if (timerColor === 'critical') return 'bg-red-500'; // Solid red for critical
    if (timerColor === 'warning') return 'bg-red-500';  // Solid red for warning
    return 'bg-white';
  };

  // Get escalation state from operatory based on alert type
  const getIsEscalated = () => {
    if (!operatory) return false;
    if (alert.alert_type === 'patient_arrived') return operatory.arrived_escalated || false;
    if (alert.alert_type === 'dr_needed') return operatory.dr_needed_escalated || false;
    if (alert.alert_type === 'assistant_help') return operatory.help_needed_escalated || false;
    return false;
  };
  
  const isEscalated = getIsEscalated();

  const getBorderClass = () => {
    if (timerColor === 'critical') {
      // Use escalated animation if toggled
      return isEscalated 
        ? 'border-red-700 animate-pulse-border-escalated' 
        : 'border-red-700 animate-pulse-border';
    }
    if (timerColor === 'warning') return 'border-red-600';
    return 'border-l-dp-500';
  };

  const handleEscalate = async () => {
    // Determine which alert type to toggle
    let alertType = 'arrived';
    if (alert.alert_type === 'dr_needed') alertType = 'dr_needed';
    else if (alert.alert_type === 'assistant_help') alertType = 'help_needed';
    
    try {
      await axios.post(`${API}/operatory/${alert.operatory_id}/toggle-escalation`, {
        alert_type: alertType
      });
      // Refetch operatory data to get updated escalation state
      const opRes = await axios.get(`${API}/operatories`);
      const op = opRes.data.find(o => o.id === alert.operatory_id);
      setOperatory(op);
      if (onStatusChange) onStatusChange();
    } catch (error) {
      console.error('Failed to toggle escalation:', error);
    }
  };

  // Handle marking alert as read
  const handleMarkRead = async () => {
    // Don't mark your own alerts as read
    if (alert.created_by === currentUserId) return;
    
    // Check if already marked as read by this user
    if (readReceipts.some(r => r.user_id === currentUserId)) return;
    
    setIsMarking(true);
    try {
      const response = await axios.post(`${API}/alerts/${alert.id}/read`);
      if (response.data.success && response.data.read_receipt) {
        setReadReceipts(prev => [...prev, response.data.read_receipt]);
      }
    } catch (error) {
      console.error('Failed to mark alert as read:', error);
    } finally {
      setIsMarking(false);
    }
  };

  // Format read receipt timestamp
  const formatReadTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  // Check if current user can mark as read (not the sender, not already read)
  const canMarkAsRead = currentUserId && 
    alert.created_by !== currentUserId && 
    !readReceipts.some(r => r.user_id === currentUserId);

  // Check if we should show the escalation button (past critical threshold)
  const showEscalationButton = timerColor === 'critical';

  const getTextColor = () => {
    if (timerColor === 'critical' || timerColor === 'warning') return 'text-white';
    return 'text-slate-800';
  };

  const handleStatusChange = async (option) => {
    // "Check Out" - ONLY status that clears operatory
    if (option.value === 'checkout') {
      try {
        // Clear the operatory state
        await axios.put(`${API}/operatories/${alert.operatory_id}/clear-state`);
        // Complete the alert
        await completeAlert(alert.id);
        if (onStatusChange) onStatusChange();
      } catch (error) {
        console.error('Failed to checkout:', error);
      }
      return;
    }

    // "Seated in Room" - Update status, dismiss popup
    if (option.value === 'seated_in_room') {
      try {
        // Update operatory state to seated_in_room
        await axios.post(`${API}/alerts`, {
          operatory_id: alert.operatory_id,
          alert_type: 'seated_in_room',
          metadata: {}
        });
        // Complete current alert (dismisses popup)
        await completeAlert(alert.id);
        fetchAlerts();
        if (onStatusChange) onStatusChange();
      } catch (error) {
        console.error('Failed to change status:', error);
      }
      return;
    }

    try {
      // Create new alert with selected status (Arrived or Dr. Needed - both have timers)
      await axios.post(`${API}/alerts`, {
        operatory_id: alert.operatory_id,
        alert_type: option.alertType,
        metadata: option.doctorId ? { doctor_id: option.doctorId, doctor_name: option.doctorName } : {}
      });
      
      // Complete current alert
      await completeAlert(alert.id);
      fetchAlerts();
      if (onStatusChange) onStatusChange();
    } catch (error) {
      console.error('Failed to change status:', error);
    }
  };

  // Build menu options - Status workflow:
  // 1. Arrived - timer starts, popup appears
  // 2. Seated in Room - popup disappears, timer stops
  // 3. Check Out - no timer, no popup
  // 4. Dr. [Name] Needed - dynamic for each doctor
  const menuOptions = [
    { label: 'Arrived', value: 'arrived', alertType: 'patient_arrived', icon: '🚪' },
    { label: 'Seated in Room', value: 'seated_in_room', alertType: 'seated_in_room', icon: '🪑' },
    ...doctors.map(doc => ({
      label: `Dr. ${doc.full_name} Needed`,
      value: `doctor_${doc.id}`,
      alertType: 'dr_needed',
      doctorId: doc.id,
      doctorName: doc.full_name,
      icon: '👨‍⚕️'
    })),
    { label: 'Check Out', value: 'checkout', alertType: 'patient_checkout', icon: '💳' }
  ];

  // Get display label for current status
  const getStatusLabel = () => {
    if (alert.alert_type === 'dr_needed' && alert.metadata?.doctor_name) {
      // doctor_name already includes "Dr." prefix, so just add "Needed"
      const npIndicator = alert.metadata?.is_new_patient ? ' (NP)' : '';
      return `${alert.metadata.doctor_name} Needed${npIndicator}`;
    }
    if ((alert.alert_type === 'help_needed' || alert.alert_type === 'assistant_help') && alert.metadata?.help_type) {
      // Show the specific help type that was selected
      const helpType = alert.metadata.help_type;
      if (helpType === 'General') return 'Help Needed';
      return helpType;
    }
    return alertTypeLabels[alert.alert_type] || alert.alert_type;
  };
  
  // Check if this is a new patient exam
  const isNewPatient = alert.alert_type === 'dr_needed' && alert.metadata?.is_new_patient;

  const handleDismiss = async () => {
    // X button ONLY closes the alert popup - does NOT affect dashboard operatory state
    await completeAlert(alert.id);
    // Note: We don't call onStatusChange() here because dashboard should not change
  };

  // Handle direct click on Dr. Needed button -> clears the dr needed status
  const handleDrNeededClick = async () => {
    try {
      // Clear the dr needed status on the operatory
      await axios.post(`${API}/operatory/${alert.operatory_id}/clear-dr-needed`);
      // Complete the alert (dismisses popup)
      await completeAlert(alert.id);
      if (onStatusChange) onStatusChange();
    } catch (error) {
      console.error('Failed to clear dr needed status:', error);
    }
  };

  // Handle direct click on Help Needed button -> clears the help needed status
  const handleHelpNeededClick = async () => {
    try {
      // Clear the help needed status on the operatory
      await axios.post(`${API}/operatory/${alert.operatory_id}/clear-help`);
      // Complete the alert (dismisses popup)
      await completeAlert(alert.id);
      if (onStatusChange) onStatusChange();
    } catch (error) {
      console.error('Failed to clear help needed status:', error);
    }
  };

  // Check if this is a patient_arrived alert - use simple click button instead of long-press menu
  const isArrivedAlert = alert.alert_type === 'patient_arrived';
  const isNextPatient = alert.metadata?.is_next_patient === true;
  
  // Track the alert's current state based on backend status
  // "active" = Arrived, "seated" = Seated
  const isSeatedState = alert.status === 'seated';

  // Handle direct click on Arrived button -> changes to Seated
  const handleArrivedToSeated = async () => {
    // Block progression for next patient alerts
    if (isNextPatient) return;
    try {
      // Call patient-seated endpoint to update operatory state
      await axios.post(`${API}/operatory/${alert.operatory_id}/patient-seated`);
      if (onStatusChange) onStatusChange();
    } catch (error) {
      console.error('Failed to change to seated:', error);
    }
  };

  // Handle click on Seated button -> progress to Dr. in Room and close alert
  const handleSeatedToDrInRoom = async () => {
    try {
      // Call dr-in-room endpoint to update operatory state (this also completes the alert)
      await axios.post(`${API}/operatory/${alert.operatory_id}/dr-in-room`);
      if (onStatusChange) onStatusChange();
    } catch (error) {
      console.error('Failed to change to dr in room:', error);
    }
  };

  // Get the button color based on alert status — uses practice button colors to match dashboard
  const arrivedColor = practice?.button_color_patient_arrived || '#0284c7';
  const seatedColor = practice?.button_color_patient_seated || '#059669';

  const getAlertButtonColor = () => {
    if (timerColor === 'critical' || timerColor === 'warning') {
      return { bg: '#ffffff', text: 'text-red-600' };
    }
    if (isSeatedState) {
      return { bg: seatedColor, text: 'text-white' };
    }
    return { bg: arrivedColor, text: 'text-white' };
  };

  // Get button label based on current status
  const getAlertButtonLabel = () => {
    if (isSeatedState) return 'Seated';
    if (isNextPatient) return 'Next Pt. Arrived';
    return 'Arrived';
  };

  // Get the click handler based on current status
  const getAlertButtonHandler = () => {
    if (isNextPatient) return () => {}; // No action for next patient alerts
    if (isSeatedState) return handleSeatedToDrInRoom;
    return handleArrivedToSeated;
  };

  return (
    <div
      data-testid={`alert-bubble-${index}`}
      className={`w-64 rounded-xl border-4 shadow-2xl overflow-hidden slide-in ${getBorderClass()} ${getBgColor()}`}
      style={{ marginBottom: index > 0 ? '12px' : '0' }}
    >
      <div className="p-3 relative">
        {/* X Button to dismiss */}
        <button
          onClick={handleDismiss}
          data-testid={`dismiss-alert-${index}`}
          className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
            timerColor === 'critical' || timerColor === 'warning'
              ? 'bg-white/20 hover:bg-white/40 text-white'
              : 'bg-slate-200/50 hover:bg-slate-300/70 text-slate-600'
          }`}
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header - Provider Name or Operatory Name */}
        <div className="flex items-center justify-between mb-2 pr-6">
          <div className="flex flex-col">
            {operatory?.current_provider_name ? (
              <>
                <div className="flex items-center gap-2">
                  <h4 className={`font-heading font-bold text-base ${getTextColor()}`}>
                    {operatory.current_provider_name}
                  </h4>
                  {/* New Patient Badge */}
                  {isNewPatient && (
                    <span className={`px-1.5 py-0.5 text-xs font-bold rounded ${
                      timerColor === 'critical' || timerColor === 'warning' 
                        ? 'bg-white/30 text-white' 
                        : 'bg-amber-500 text-white'
                    }`}>
                      NP
                    </span>
                  )}
                </div>
                <span className={`text-xs ${
                  timerColor === 'critical' || timerColor === 'warning' ? 'text-white/70' : 'text-slate-500'
                }`}>
                  Operatory {operatory.number}
                </span>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <h4 className={`font-heading font-bold text-base ${getTextColor()}`}>
                  {operatory?.name || 'Operatory'}
                </h4>
                {/* New Patient Badge */}
                {isNewPatient && (
                  <span className={`px-1.5 py-0.5 text-xs font-bold rounded ${
                    timerColor === 'critical' || timerColor === 'warning' 
                      ? 'bg-white/30 text-white' 
                      : 'bg-amber-500 text-white'
                  }`}>
                    NP
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Escalation button - appears when past critical threshold */}
            {showEscalationButton && (
              <button
                onClick={handleEscalate}
                data-testid={`escalate-alert-${index}`}
                className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                  isEscalated 
                    ? 'ring-2 ring-red-400 scale-110' 
                    : 'hover:scale-105'
                }`}
                style={{ backgroundColor: '#ffffff' }}
                title={isEscalated ? 'Click to reduce urgency' : 'Click to escalate'}
              >
                <AlertCircle className={`w-5 h-5 ${isEscalated ? 'text-red-600' : 'text-red-500'}`} />
              </button>
            )}
            <div className={`text-xl font-mono font-bold tabular-nums ${
              timerColor === 'critical' || timerColor === 'warning' ? 'text-white' : 'text-dp-600'
            }`}>
              {formatTime(elapsed)}
            </div>
          </div>
        </div>

        {/* Message display - if alert has a message attached */}
        {alert.metadata?.message && (
          <div className={`mb-2 p-2 rounded-lg text-sm ${
            timerColor === 'critical' || timerColor === 'warning'
              ? 'bg-white/20 text-white'
              : 'bg-slate-100 text-slate-700'
          }`}>
            <p className="italic">"{alert.metadata.message}"</p>
          </div>
        )}

        {/* Status Button - For Arrived alerts: stepped progression (Arrived -> Seated -> Dr. in Room) */}
        {/* For Dr. Needed alerts: simple click to clear */}
        {/* For Help Needed alerts: simple click to clear */}
        {isArrivedAlert ? (
          <button
            onClick={getAlertButtonHandler()}
            data-testid={`arrived-to-seated-btn-${index}`}
            className={`w-full font-semibold rounded-lg text-sm py-2.5 transition-colors ${isNextPatient ? 'cursor-default opacity-90' : 'hover:brightness-95 cursor-pointer'} ${getAlertButtonColor().text}`}
            style={{ backgroundColor: getAlertButtonColor().bg }}
          >
            {getAlertButtonLabel()}
          </button>
        ) : alert.alert_type === 'dr_needed' ? (
          <button
            onClick={handleDrNeededClick}
            data-testid={`dr-needed-btn-${index}`}
            className={`w-full font-semibold rounded-lg text-sm py-2.5 transition-colors ${
              timerColor === 'critical' || timerColor === 'warning' 
                ? 'text-red-600 hover:brightness-95' 
                : 'bg-amber-500 text-white hover:bg-amber-600'
            }`}
            style={timerColor === 'critical' || timerColor === 'warning' ? { backgroundColor: '#ffffff' } : {}}
          >
            {getStatusLabel()}
          </button>
        ) : alert.alert_type === 'assistant_help' ? (
          <button
            onClick={handleHelpNeededClick}
            data-testid={`help-needed-btn-${index}`}
            className={`w-full font-semibold rounded-lg text-sm py-2.5 transition-colors ${
              timerColor === 'critical' || timerColor === 'warning' 
                ? 'text-red-600 hover:brightness-95' 
                : 'bg-purple-500 text-white hover:bg-purple-600'
            }`}
            style={timerColor === 'critical' || timerColor === 'warning' ? { backgroundColor: '#ffffff' } : {}}
          >
            {getStatusLabel()}
          </button>
        ) : (
          <LongPressMenu
            options={menuOptions}
            onSelect={handleStatusChange}
            className={`w-full font-semibold rounded-lg text-sm py-2.5 ${
              timerColor === 'critical' || timerColor === 'warning' 
                ? 'text-red-600 hover:brightness-95' 
                : 'bg-emerald-600 text-white hover:bg-emerald-700'
            }`}
            style={timerColor === 'critical' || timerColor === 'warning' ? { backgroundColor: '#ffffff' } : {}}
          >
            {getStatusLabel()}
          </LongPressMenu>
        )}

        {/* Read Button and Read Receipts */}
        <div className="mt-2 flex items-center justify-between">
          {/* Read Button - Bottom Left */}
          {canMarkAsRead && (
            <button
              onClick={handleMarkRead}
              disabled={isMarking}
              data-testid={`read-alert-${index}`}
              className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-all ${
                timerColor === 'critical' || timerColor === 'warning'
                  ? 'bg-white/20 text-white hover:bg-white/30'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <CheckCheck className="w-3.5 h-3.5" />
              {isMarking ? 'Marking...' : 'Read'}
            </button>
          )}
          
          {/* Read Receipts Display - Bottom Right */}
          {readReceipts.length > 0 && (
            <div className={`flex flex-col items-end gap-0.5 text-xs ${
              timerColor === 'critical' || timerColor === 'warning'
                ? 'text-white/80'
                : 'text-slate-500'
            }`}>
              {readReceipts.map((receipt, idx) => (
                <div key={idx} className="flex items-center gap-1">
                  <CheckCheck className="w-3 h-3 text-dp-500" />
                  <span>{receipt.user_name}</span>
                  <span className="opacity-70">{formatReadTime(receipt.read_at)}</span>
                </div>
              ))}
            </div>
          )}
          
          {/* Spacer when no read button or receipts */}
          {!canMarkAsRead && readReceipts.length === 0 && <div />}
        </div>

        {/* Quick Responses from Watch */}
        {quickResponses.length > 0 && (
          <div className={`mt-2 pt-2 border-t ${
            timerColor === 'critical' || timerColor === 'warning'
              ? 'border-white/20'
              : 'border-slate-200'
          }`}>
            <div className={`text-xs space-y-1 ${
              timerColor === 'critical' || timerColor === 'warning'
                ? 'text-white/90'
                : 'text-slate-600'
            }`}>
              {quickResponses.slice(-3).map((response, idx) => (
                <div key={idx} className="flex items-center gap-1.5">
                  <span className="font-medium">{response.user_name}:</span>
                  <span className={`px-1.5 py-0.5 rounded ${
                    timerColor === 'critical' || timerColor === 'warning'
                      ? 'bg-white/20'
                      : 'bg-emerald-100 text-emerald-700'
                  }`}>
                    {response.message}
                  </span>
                  <span className="opacity-60">{formatReadTime(response.responded_at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AlertStack({ onStatusChange }) {
  const { alerts, fetchAlerts } = useAlerts();
  const { user } = useAuth();
  const { socket } = useSocket();
  const currentUserId = user?.id;

  // Listen for read receipt and quick response WebSocket events
  useEffect(() => {
    const handleAlertRead = (data) => {
      if (data.created_by === currentUserId) {
        playSoundForAlert('read_receipt');
      }
      fetchAlerts();
    };

    const handleQuickResponse = (data) => {
      if (data.created_by === currentUserId) {
        playSoundForAlert('read_receipt');
      }
      fetchAlerts();
    };

    if (socket) {
      socket.on('alert_read', handleAlertRead);
      socket.on('quick_response_received', handleQuickResponse);
      return () => {
        socket.off('alert_read', handleAlertRead);
        socket.off('quick_response_received', handleQuickResponse);
      };
    }
  }, [currentUserId, fetchAlerts, socket]);

  // Sort by elapsed time (oldest first)
  const sortedAlerts = [...alerts].sort((a, b) => {
    const aCreated = new Date(a.created_at);
    const bCreated = new Date(b.created_at);
    return aCreated - bCreated;
  });

  if (sortedAlerts.length === 0) return null;

  // Calculate scale factor based on number of alerts to fit on screen
  // Assuming each alert is ~140px tall with spacing, and viewport height is ~1080px
  const availableHeight = window.innerHeight - 100; // Leave space for margins
  const alertHeight = 140; // Approximate height per alert with spacing
  const totalNeededHeight = sortedAlerts.length * alertHeight;
  const scaleFactor = totalNeededHeight > availableHeight 
    ? Math.max(0.6, availableHeight / totalNeededHeight) // Min scale 60%, max 100%
    : 1;

  return (
    <div 
      className="fixed bottom-8 right-8 flex flex-col-reverse gap-0 z-50" 
      data-testid="alert-stack"
      style={{
        transform: `scale(${scaleFactor})`,
        transformOrigin: 'bottom right'
      }}
    >
      {sortedAlerts.map((alert, index) => (
        <AlertBubble 
          key={alert.id} 
          alert={alert} 
          index={index} 
          onStatusChange={onStatusChange} 
          currentUserId={currentUserId}
        />
      ))}
    </div>
  );
}
